(() => {
  const DUTY_ROOT_SELECTOR = '[data-duty-variant-cta]';
  const STICKY_SELECTOR = '[data-duty-sticky-buy-bar]';
  const FALLBACK_WEIGHT_BY_DUTY_COUNT = {
    5: 2000,
    10: 4000,
    15: 6000,
  };

  const variantWeights = new Map();
  let latestDetail = null;

  function formatMoney(cents) {
    const locale = document.documentElement.lang || 'de-DE';
    const currency = window.Shopify?.currency?.active || 'EUR';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
    }
  }

  function calculateShippingCents(totalWeightGrams) {
    if (totalWeightGrams >= 9000) return 990;
    if (totalWeightGrams >= 6000) return 890;
    if (totalWeightGrams >= 3000) return 790;
    if (totalWeightGrams >= 1000) return 690;
    return 490;
  }

  function getSelectedVariantButton(root) {
    return root?.querySelector('[data-duty-variant-button].is-selected') || null;
  }

  function getQuantity(root) {
    const input = root?.querySelector('[data-duty-quantity-input]');
    const parsed = Number.parseInt(input?.value || '1', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  function getDutyCount(root, detail) {
    const fromEvent = Number(detail?.dutyCount);
    if (Number.isFinite(fromEvent) && fromEvent > 0) return fromEvent;

    const button = getSelectedVariantButton(root);
    const fromButton = Number.parseInt(button?.dataset.dutyCount || '', 10);
    return Number.isFinite(fromButton) && fromButton > 0 ? fromButton : 5;
  }

  function getVariantId(root) {
    const button = getSelectedVariantButton(root);
    return String(button?.dataset.variantId || '');
  }

  function getVariantWeightGrams(root, dutyCount) {
    const variantId = getVariantId(root);
    const fromProduct = variantWeights.get(variantId);

    if (Number.isFinite(fromProduct) && fromProduct > 0) return fromProduct;
    return FALLBACK_WEIGHT_BY_DUTY_COUNT[dutyCount] || 0;
  }

  function getState(root, detail) {
    const dutyCount = getDutyCount(root, detail);
    const quantityFromEvent = Number(detail?.quantity);
    const quantity = Number.isFinite(quantityFromEvent) && quantityFromEvent > 0
      ? quantityFromEvent
      : getQuantity(root);
    const variantWeightGrams = getVariantWeightGrams(root, dutyCount);
    const totalWeightGrams = variantWeightGrams * quantity;
    const shippingCents = calculateShippingCents(totalWeightGrams);

    return {
      dutyCount,
      quantity,
      variantWeightGrams,
      totalWeightGrams,
      shippingCents,
    };
  }

  function ensureMainShippingNote(root) {
    const form = root?.querySelector('[data-duty-form]');
    if (!form) return null;

    let note = form.querySelector('.find-duty-variant-cta__shipping-note');

    if (!note) {
      note = document.createElement('p');
      note.className = 'find-duty-variant-cta__shipping-note';
      const purchase = form.querySelector('.find-duty-variant-cta__purchase');
      if (purchase) purchase.insertAdjacentElement('afterend', note);
      else form.appendChild(note);
    }

    if (!note.querySelector('[data-duty-shipping-amount]')) {
      note.textContent = '';

      const primary = document.createElement('strong');
      primary.append('Versand für diese Auswahl: ');

      const amount = document.createElement('span');
      amount.dataset.dutyShippingAmount = '';
      primary.appendChild(amount);
      primary.append(' · DHL');

      const disclaimer = document.createElement('span');
      disclaimer.className = 'find-duty-shipping-disclaimer';
      disclaimer.textContent = 'Endgültige Versandkosten im Checkout; weitere Warenkorbpositionen oder das Lieferland können den Betrag ändern.';

      note.appendChild(primary);
      note.appendChild(disclaimer);
    }

    return note;
  }

  function ensureStickyShipping() {
    const sticky = document.querySelector(STICKY_SELECTOR);
    const price = sticky?.querySelector('.find-duty-sticky-buy__price');
    if (!price) return null;

    let shipping = price.querySelector('[data-duty-sticky-shipping]');
    if (!shipping) {
      shipping = document.createElement('span');
      shipping.className = 'find-duty-sticky-buy__shipping';
      shipping.dataset.dutyStickyShipping = '';
      shipping.title = 'Versand für diese Duty-Auswahl. Der Checkout ist verbindlich; weitere Warenkorbpositionen oder das Lieferland können den Betrag ändern.';
      price.appendChild(shipping);
    }

    return shipping;
  }

  function render(root, detail) {
    if (!root) return;

    const state = getState(root, detail || latestDetail);
    const note = ensureMainShippingNote(root);
    const amount = note?.querySelector('[data-duty-shipping-amount]');
    const stickyShipping = ensureStickyShipping();

    if (amount) amount.textContent = formatMoney(state.shippingCents);

    if (stickyShipping) {
      stickyShipping.textContent = `Versand ${formatMoney(state.shippingCents)}*`;
      stickyShipping.setAttribute(
        'aria-label',
        `Versand für diese Duty-Auswahl ${formatMoney(state.shippingCents)}. Maßgeblich ist der Checkout.`
      );
    }
  }

  function getProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/i);
    return match?.[1] || '';
  }

  async function loadVariantWeights() {
    const handle = getProductHandle();
    if (!handle) return;

    const rootPath = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const url = `${rootPath}products/${encodeURIComponent(handle)}.js`;

    try {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) return;
      const product = await response.json();

      (product?.variants || []).forEach((variant) => {
        const id = String(variant?.id || '');
        const grams = Number(variant?.grams);
        if (id && Number.isFinite(grams) && grams > 0) {
          variantWeights.set(id, grams);
        }
      });
    } catch (error) {
      // Fallback weights are intentionally kept so the storefront remains usable.
    }
  }

  async function init() {
    const root = document.querySelector(DUTY_ROOT_SELECTOR);
    if (!root) return;

    render(root, latestDetail);
    await loadVariantWeights();
    render(root, latestDetail);
  }

  if (!window.__findDutyShippingBridgeBound) {
    window.__findDutyShippingBridgeBound = true;

    document.addEventListener('find:duty-config-updated', (event) => {
      const root = event.target?.closest?.(DUTY_ROOT_SELECTOR) || document.querySelector(DUTY_ROOT_SELECTOR);
      if (!root) return;

      latestDetail = event.detail || null;
      render(root, latestDetail);
    });

    document.addEventListener('shopify:section:load', () => {
      window.requestAnimationFrame(init);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
